/**
 * The run that is happening right now, entity by entity.
 *
 * Every entity the run was ASKED for gets a row, not only the ones it has
 * finished. A table that grows a row every few seconds cannot say how much is
 * left, and "nothing is happening" and "invoices have not started yet" would
 * look the same - which is exactly how a long import gets cancelled halfway by
 * somebody who thought it had stalled.
 */
import { Loader2, TriangleAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImportEntityStat } from "../../../../lib/crmImport";
import { Card, CardHead, StatusPill, fmtNum } from "../../../ui";
import { entityLabel, statusLabel, statusTone } from "./labels";
import type { RunWatch } from "./useImportRun";

/** Milliseconds as seconds with one decimal. A unit symbol, so it needs no
 *  catalogue entry in either language. */
function fmtMs(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

export function RunProgress({ watch }: { watch: RunWatch }) {
  const t = useT();
  const run = watch.run;
  if (!run) return null;

  const stats = new Map<string, ImportEntityStat>(run.entityStats.map((s) => [s.entity, s]));
  // The asked-for list is the spine; anything the server reported outside it
  // still draws, because a stat with no matching request is a fact and hiding
  // it would make the totals disagree with the rows.
  const extra = run.entityStats.filter((s) => !run.entities.includes(s.entity)).map((s) => s.entity);
  const rows = [...run.entities, ...extra];

  return (
    <Card>
      <CardHead
        title={t("crmImport.progress.title")}
        sub={t(run.dryRun ? "crmImport.progress.dryRunNote" : "crmImport.progress.realNote")}
        actions={
          <div className="flex items-center gap-2.5">
            {run.dryRun ? <StatusPill tone="info">{t("crmImport.dryRunBadge")}</StatusPill> : null}
            <StatusPill tone={statusTone(run.status)}>{statusLabel(t, run.status)}</StatusPill>
            {run.status === "running" ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
          </div>
        }
      />

      <div className="px-6 pb-6 pt-1">
        <div className="crm-cov-scroll">
          <table className="crm-cov-tbl">
            <thead>
              <tr>
                <th scope="col">{t("crmImport.entities.entity")}</th>
                <th scope="col">{t("crmImport.progress.read")}</th>
                <th scope="col">{t("crmImport.progress.created")}</th>
                <th scope="col">{t("crmImport.progress.updated")}</th>
                <th scope="col">{t("crmImport.progress.skipped")}</th>
                <th scope="col">{t("crmImport.progress.time")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entity) => {
                const stat = stats.get(entity);
                return (
                  <tr key={entity}>
                    <th scope="row">{entityLabel(t, entity)}</th>
                    {stat ? (
                      <>
                        <td>{fmtNum(stat.read)}</td>
                        <td>{fmtNum(stat.created)}</td>
                        <td>{fmtNum(stat.updated)}</td>
                        <td>{fmtNum(stat.skipped)}</td>
                        <td>{fmtMs(stat.ms)}</td>
                      </>
                    ) : (
                      <td colSpan={5} className="opacity-60">
                        {t("crmImport.progress.pending")}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {run.error ? (
          <p className="mt-4 flex items-start gap-2 break-words text-[12.5px] text-destructive">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            {run.error}
          </p>
        ) : null}

        {run.issues.length > 0 ? (
          <div className="mt-4">
            <span className="crm-lab !mt-0">{t("crmImport.progress.issues")}</span>
            <ul className="mt-1.5 space-y-1">
              {run.issues.slice(0, 20).map((issue) => (
                <li key={issue.id} className="break-words text-[12px] leading-snug text-muted-foreground">
                  <b className="font-semibold text-foreground">{entityLabel(t, issue.entity)}</b>{" "}
                  {issue.legacyId ? `#${issue.legacyId} ` : ""}
                  {issue.reason}
                  {issue.detail ? ` - ${issue.detail}` : ""}
                </li>
              ))}
            </ul>
            {run.issues.length > 20 ? (
              <p className="crm-sub mt-1.5 text-[11.5px]">{t("crmImport.progress.issuesMore")}</p>
            ) : null}
          </div>
        ) : null}

        {watch.gaveUp ? (
          <p className="mt-4 text-[12.5px] text-destructive">{t("crmImport.progress.lost")}</p>
        ) : watch.stale ? (
          <p className="crm-sub mt-4 text-[11.5px]">{t("crmImport.progress.stale")}</p>
        ) : null}
      </div>
    </Card>
  );
}
